import { isMobile, isDesktop } from 'react-device-detect';

import type { AnyRoute } from '@tanstack/react-router';
import { createRootRoute, createRoute, createRouter, redirect } from '@tanstack/react-router';

import casinoConfig from '@dcentralexpedientedigital/config';
import type { IBlock } from '@dcentralexpedientedigital/fe-shared/types/config.interface';
import { PLATFORM } from '@dcentralexpedientedigital/fe-shared/types/platform.types';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import groupBy from 'map.groupby/shim';

import { AuthGuardedPageRenderer } from './components/auth-guarded-page-renderer';
import { CatchAllRedirect } from './components/catch-all-redirect';
import { Layout } from './components/layout';
import { PageRenderer } from './components/page-renderer';
import { generateAllPaths } from './utils/generate-all-paths';
import { getLocaleFromStoreOrGeolocation } from './utils/get-locale-from-store-or-geo';
import { isBlocks } from './utils/is-blocks';
import { isValidLocale } from './utils/language';
import { validateLanguageRedirect } from './utils/validate-language-redirect';

// Add groupBy to the Map prototype
groupBy();

export interface ModalSearch {
    modal?: string;
}

const rootRoute = createRootRoute({
    validateSearch: (search: Record<string, unknown>): ModalSearch => {
        return {
            modal: typeof search.modal === 'string' ? search.modal : undefined,
        };
    },
});

const redirectToDefaultLangRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/',
    beforeLoad: async ({ location, params }) => {
        const locale = getLocaleFromStoreOrGeolocation();

        const pathnameWithoutLocale = location.pathname.replace(/^\/[a-z]{2}(?:-[a-z]{2})?/i, '');

        const normalizedPath = `${pathnameWithoutLocale}`.replace(/\/+$/, '');

        throw redirect({
            // ensure it becomes /en-gb or /en-gb/something
            to: `/${locale}${normalizedPath || ''}`,
            // preserve query string like ?modal=deposit
            search: location.search,
            params,
        });
    },
});

const langRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/$lang',
    beforeLoad: async ({ location, params }) => {
        if (!isValidLocale(params.lang)) {
            const locale = getLocaleFromStoreOrGeolocation();
            throw redirect({
                to: `/${locale}${location.pathname}`,
                search: location.search,
            });
        }
    },
});

const catchAllRoute = createRoute({
    getParentRoute: () => langRoute,
    path: '*',
    component: CatchAllRedirect,
});

const layouts = Object.keys(casinoConfig.pageLayouts).reduce(
    (acc, layoutName) => {
        const layout =
            casinoConfig.pageLayouts[layoutName as keyof typeof casinoConfig.pageLayouts];
        acc[layoutName] = createRoute({
            getParentRoute: () => langRoute,
            id: layoutName,
            component: Layout,
            staticData: {
                isLayout: true,
                declaration: layout,
                configuration: layout.components.reduce(
                    (acc, componentName) => {
                        acc[componentName] =
                            (casinoConfig.layoutConfig as unknown as Record<string, IBlock[]>)[
                                componentName
                            ] ?? [];
                        return acc;
                    },
                    {} as Record<string, IBlock[]>,
                ),
            },
        });
        return acc;
    },
    {} as Record<string, AnyRoute>,
);

const allPages = casinoConfig.pages.flatMap(
    ({
        id,
        name,
        urlPath,
        blocks,
        layout,
        redirectTo,
        openInModal,
        breadcrumbs,
        header,
        preserveScroll = false,
        isSystemRoute = false,
        autoScrollOptions,
        requireAuth = false,
        excludePlatforms = [],
    }) => {
        const currentPlatform = isMobile
            ? PLATFORM.MOBILE
            : isDesktop
              ? PLATFORM.DESKTOP
              : PLATFORM.TABLET;

        const isExcluded = excludePlatforms.includes(currentPlatform);

        if (isExcluded && !redirectTo) {
            return [];
        }

        const urlPaths = generateAllPaths(urlPath);
        const existingPaths: Record<string, boolean> = {};
        const component = requireAuth ? AuthGuardedPageRenderer : PageRenderer;

        return urlPaths
            .filter(({ path }) => !existingPaths[path] && (existingPaths[path] = true))
            .map(({ path }) => ({
                route: createRoute({
                    getParentRoute: () => layouts[layout],
                    beforeLoad: validateLanguageRedirect(urlPaths, redirectTo),
                    path,
                    component,
                    staticData: {
                        blocks,
                        requireAuth,
                        isSystemRoute,
                        openInModal,
                        preserveScroll,
                        autoScrollOptions,
                        breadcrumbs,
                        header,
                        id,
                        pageId: name,
                    },
                }),
                layout,
            }));
    },
);

const pagesByLayout = Map.groupBy(allPages, (item) => item.layout);

const routeTree = rootRoute.addChildren([
    redirectToDefaultLangRoute,
    langRoute.addChildren([
        ...Array.from(pagesByLayout.keys()).map((key) =>
            layouts[key].addChildren([
                ...(pagesByLayout.get(key)?.map(({ route }) => route) ?? []),
            ]),
        ),
    ]),
    catchAllRoute,
]);

export const router = createRouter({
    routeTree,
    context: {
        getRoute: (pathname: string, search: Record<string, unknown>) => {
            const matches = router.matchRoutes(pathname, search);
            return matches.find((m) => isBlocks(m.staticData))?.staticData;
        },
    },
    scrollRestoration: true,
    // Disable native hash scrolling - fires before async content loads.
    // Hash scrolling is handled by useScrollToLocation with element polling.
    defaultHashScrollIntoView: false,
});
